import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { actionStrings, MomentStateContext } from '@/v2/state/momentState';
import { ApplicationContext, DriverAssistContext } from '@/v2/state/providers';
import { AnalyticsContext } from '@/v2/components/application/Analytics';
import ProgressIndicator from '@/v2/components/atoms/ProgressIndicator';
import getScenesJourney, { NON_CONTRIBUTORS, PROGRESS_EXCEPTIONS } from '../Scenes/getScenesJourney';

const propTypes = {
    children: PropTypes.node.isRequired,
};

const getActiveItemProgress = ({ sceneMenuMoments, menuMomentIndex, currentProgress, state }) => {
    const progressItems = sceneMenuMoments.map((name, index) => {
        const itemActive = index === menuMomentIndex;
        const progress = itemActive ? currentProgress : 0;
        const visited = itemActive || state?.[name];

        return {
            active: itemActive,
            disableProgress: PROGRESS_EXCEPTIONS.includes(name),
            name,
            progress,
            visited,
        };
    });

    return progressItems;
};

const getUpdatedActiveItemProgress = ({ indicatorItems, activeItemIndex, progress }) => {
    const progressItems = indicatorItems.map((item, index) => {
        if (index === activeItemIndex) {
            return {
                ...item,
                progress,
            };
        }

        return { ...item };
    });

    return progressItems;
};

const getCurrentProgressPercent = (itemIndex, totalItems) => Math.ceil((itemIndex / totalItems) * 100);

const ProgressHandler = ({ children }) => {
    const { state, dispatch } = useContext(MomentStateContext);
    const [applicationData] = useContext(ApplicationContext);
    const [driverAssistData] = useContext(DriverAssistContext);
    const { trackEvent } = useContext(AnalyticsContext);
    const { activeMomentProgress, progressBarDarkThemeEnabled } = state;

    const location = useLocation();

    const [indicatorItems, setIndicatorItems] = useState([]);
    const [activeItemIndex, setActiveItemIndex] = useState(-1);

    const [currentProgress, setCurrentProgress] = useState(0);
    const [currentProgressItemsCount, setCurrentProgressItemsCount] = useState(0);

    // check in Menu  & Driver Assist data for enabled scenes & update JOURNEY accordingly
    const driverAssistMenuData = applicationData?.header?.menu?.items?.find((item) => item.latestRoute === 'driverAssist.html')?.submenu;
    const JOURNEY = getScenesJourney({ driverAssistScenes: driverAssistData?.scenes, driverAssistMenuData });

    const getIntermediateMomentIndex = (sceneMenuMoments, sceneName, momentName) =>
        sceneMenuMoments?.findIndex((subRoute) => {
            const subMenuActiveItems = JOURNEY[sceneName][subRoute].filter((itemName) => !NON_CONTRIBUTORS.includes(itemName));
            const subMenuActiveItemIndex = subMenuActiveItems.findIndex((itemName) => itemName === momentName);
            const currProgress = subMenuActiveItemIndex > -1 ? getCurrentProgressPercent(subMenuActiveItemIndex, subMenuActiveItems.length) : 0;

            setCurrentProgressItemsCount(subMenuActiveItems.length);
            setCurrentProgress(currProgress);

            return subRoute === momentName || JOURNEY[sceneName][subRoute].includes(momentName);
        });

    const updateOnLocationChange = (sceneName, momentName) => {
        const sceneMenuMoments = Object.keys(JOURNEY[sceneName]);
        const menuMomentIndex = getIntermediateMomentIndex(sceneMenuMoments, sceneName, momentName);

        // either the exact menu item OR sub-moment of an "active" menu moment sets the main menu moment state to visited
        const visitedMomentAction = actionStrings[momentName] || actionStrings[sceneMenuMoments[menuMomentIndex]];

        // set visited as soon as arrive on specific route, except for viz. viz is visited on interaction
        if (visitedMomentAction && !PROGRESS_EXCEPTIONS.includes(momentName)) {
            dispatch({ type: visitedMomentAction });
        }

        if (sceneMenuMoments.length && menuMomentIndex > -1) {
            const activeItemProgress = getActiveItemProgress({
                sceneMenuMoments,
                menuMomentIndex,
                currentProgress,
                state,
            });
            setIndicatorItems(activeItemProgress);
            setActiveItemIndex(menuMomentIndex);
        }
    };

    const onVisualizerInteracted = () => {
        dispatch({ type: actionStrings.exterior });
    };

    useEffect(() => {
        window.addEventListener('visualizerInteracted', onVisualizerInteracted);

        dispatch({ type: actionStrings.activeMomentProgress, payload: 0 });

        return () => {
            window.removeEventListener('visualizerInteracted', onVisualizerInteracted);

            dispatch({ type: actionStrings.activeMomentProgress, payload: 0 });
        };
    }, []);

    useEffect(() => {
        const sceneName = location.pathname?.slice(1).split('.')[0];
        const momentName = location.hash?.slice(1);
        updateOnLocationChange(sceneName, momentName);
        if (momentName) {
            trackEvent('vcClick', {
                vcStep: sceneName,
                vcClick: `slide:${momentName}`,
            });
        }
    }, [location.pathname, location.hash]);

    useEffect(() => {
        if (currentProgress > 0) {
            const updatedActiveItemProgress = getUpdatedActiveItemProgress({ indicatorItems, activeItemIndex, progress: currentProgress });

            setIndicatorItems(updatedActiveItemProgress);
        }
        dispatch({ type: actionStrings.activeMomentProgress, payload: 0 });
    }, [currentProgress]);

    useEffect(() => {
        if (indicatorItems.length && activeItemIndex > -1) {
            const updatedProgress = currentProgress + Math.ceil(activeMomentProgress / currentProgressItemsCount);
            const updatedActiveItemProgress = getUpdatedActiveItemProgress({ indicatorItems, activeItemIndex, progress: updatedProgress });

            setIndicatorItems(updatedActiveItemProgress);
        }
    }, [activeMomentProgress]);

    return (
        <>
            <ProgressIndicator dark={progressBarDarkThemeEnabled} items={indicatorItems} />
            {children}
        </>
    );
};

ProgressHandler.propTypes = propTypes;

export default ProgressHandler;
